/**
 * Client für den zentralen Nachrichten-Service (rmnc-nachrichten) —
 * Berichte & Kommunikation zu Tickets und KVP-Aufgaben.
 *
 * Anders als lib/api.ts geht dieser Client CROSS-ORIGIN direkt an den
 * Nachrichten-Service (VITE_NACHRICHTEN_API_URL) und nutzt ein eigenes
 * Access-Token (Logto-Resource VITE_LOGTO_NACHRICHTEN_RESOURCE, Provider in
 * auth/store.ts). Ohne konfigurierte URL ist das Feature aus
 * (NACHRICHTEN_ENABLED=false → BerichtsThread rendert nichts).
 */

import axios from "axios"
import { AUTH_MODE, getNachrichtenAccessToken } from "@/auth/store"

const BASE_URL: string =
  import.meta.env.VITE_NACHRICHTEN_API_URL ??
  (import.meta.env.DEV ? "http://localhost:8050" : "")

export const NACHRICHTEN_ENABLED = Boolean(BASE_URL)

export const nachrichtenApi = axios.create({
  baseURL: `${BASE_URL.replace(/\/$/, "")}/api/v1`,
})

nachrichtenApi.interceptors.request.use(async (config) => {
  if (AUTH_MODE === "dev") {
    config.headers["X-Dev-User"] = JSON.stringify({
      sub: "dev-admin",
      name: "Dev Admin",
      roles: ["admin"],
    })
    return config
  }
  const token = await getNachrichtenAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// --- Typen (CamelCase-Verträge des Nachrichten-Service) ---------------------

export type NachrichtTyp = "kommentar" | "zwischenbericht" | "abschlussbericht" | "system"

export interface NachrichtenThread {
  id: string
  app: string
  objektTyp: string
  objektId: string
  titel: string
  url: string
  status: "offen" | "abgeschlossen"
  abgeschlossenAm: string | null
}

export interface ThreadTeilnehmer {
  sub: string | null
  anzeigeName: string
  rolle: "ersteller" | "verantwortlich" | "info"
  aktiv: boolean
}

export interface ThreadDetail extends NachrichtenThread {
  teilnehmer: ThreadTeilnehmer[]
}

export interface Nachricht {
  id: string
  threadId: string
  typ: NachrichtTyp
  verfasserSub: string | null
  verfasserName: string
  body: string
  metadaten: Record<string, unknown> | null
  emailAngefordert: boolean
  erstelltAm: string
}

export interface NachrichtErgebnis {
  nachricht: Nachricht
  threadStatus: "offen" | "abgeschlossen"
  emailErgebnis: string | null
}

// --- Aufrufe -----------------------------------------------------------------

/** Thread zu einem Vorgang finden (null bei 404 = noch kein Thread). */
export async function lookupThread(
  objektTyp: "ticket" | "kvp_aufgabe",
  objektId: string,
): Promise<ThreadDetail | null> {
  try {
    const { data } = await nachrichtenApi.get<ThreadDetail>("/threads/lookup", {
      params: { app: "technik", objektTyp, objektId },
    })
    return data
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}

export async function fetchNachrichten(threadId: string): Promise<Nachricht[]> {
  const { data } = await nachrichtenApi.get<Nachricht[]>(`/threads/${threadId}/nachrichten`)
  return data
}

export async function posteNachricht(
  threadId: string,
  payload: { typ: NachrichtTyp; body: string; auchPerEmail: boolean },
): Promise<NachrichtErgebnis> {
  const { data } = await nachrichtenApi.post<NachrichtErgebnis>(
    `/threads/${threadId}/nachrichten`,
    payload,
  )
  return data
}

export async function markiereGelesen(threadId: string): Promise<void> {
  await nachrichtenApi.post(`/threads/${threadId}/gelesen`)
}
